import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';

// Components
import { defineMessages, FormattedMessage, useIntl } from 'dibs-react-intl';
import ShareIcon from 'dibs-icons/exports/Share';

import { PdpSharedIconBubblePinterest } from '../PdpSharedIconBubbles/PdpSharedIconBubblePinterest';
import { PdpSharedIconBubbleFacebook } from '../PdpSharedIconBubbles/PdpSharedIconBubbleFacebook';
import { PdpSharedIconBubbleEmail } from '../PdpSharedIconBubbles/PdpSharedIconBubbleEmail';

import { PdpRespIconBubbleShare_viewer$key } from './__generated__/PdpRespIconBubbleShare_viewer.graphql';
import { PdpRespIconBubbleShare_user$key } from './__generated__/PdpRespIconBubbleShare_user.graphql';
import { PdpRespIconBubbleShare_item$key } from './__generated__/PdpRespIconBubbleShare_item.graphql';

import { PdpRespIconBubbleWithTooltip } from './PdpRespIconBubbleWithTooltip';

// messages
const messages = defineMessages({
    share: { id: 'PdpRespIconBubbleShare.icon', defaultMessage: 'Share' },
});

const pinterestLabel = (
    <FormattedMessage
        id="PdpRespIconBubbleShare.pinterestCta"
        defaultMessage="Share on Pinterest"
    />
);

const facebookLabel = (
    <FormattedMessage id="PdpRespIconBubbleShare.facebookCta" defaultMessage="Share on Facebook" />
);

const emailLabel = (
    <FormattedMessage id="PdpRespIconBubbleShare.emailCta" defaultMessage="Share via Email" />
);

type Props = {
    item: PdpRespIconBubbleShare_item$key;
    viewer: PdpRespIconBubbleShare_viewer$key;
    user?: PdpRespIconBubbleShare_user$key | null;
    skuId?: string;
};

const itemFragment = graphql`
    fragment PdpRespIconBubbleShare_item on Item {
        ...PdpSharedIconBubblePinterest_item
        ...PdpSharedIconBubbleFacebook_item
        ...PdpSharedIconBubbleEmail_item
    }
`;

const viewerFragment = graphql`
    fragment PdpRespIconBubbleShare_viewer on Viewer {
        ...PdpSharedIconBubbleEmail_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespIconBubbleShare_user on User {
        ...PdpSharedIconBubbleEmail_user
    }
`;

export const PdpRespIconBubbleShare: FC<Props> = ({
    skuId,
    viewer: viewerRef,
    item: itemRef,
    user: userRef = null,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const intl = useIntl();

    return (
        <PdpRespIconBubbleWithTooltip icon={ShareIcon} label={intl.formatMessage(messages.share)}>
            <PdpSharedIconBubblePinterest item={item} label={pinterestLabel} />
            <PdpSharedIconBubbleFacebook item={item} label={facebookLabel} />
            <PdpSharedIconBubbleEmail
                item={item}
                user={user}
                viewer={viewer}
                skuId={skuId}
                label={emailLabel}
            />
        </PdpRespIconBubbleWithTooltip>
    );
};
